import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import classNames from 'classnames'
import { ApiRetailerPaymentType } from '@vend/types'
import { useT } from '@transifex/react'
import { vn } from 'vend-number'
import { PAYMENT_TYPES } from '@vend/business-components'
import { useLocalizedPaymentTypeName } from '@vend/react-utilities'
import { useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleFinals,
  selectLspCashDiscountPercentage,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { applyLspCashDiscountToOutstanding } from '../../../functions/payments'
import { useHotkeysService } from '../../../ng-compat/hooks'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { usePaymentDisabilityCheck } from './hooks/usePaymentDisabilityCheck'
import { useLspCashDiscountBlockedSale } from './hooks/useLspCashDiscountBlockedSale'
import { isLspCashDiscountBlocked } from './isPartialAmountTendered'
import { usePaymentRecalculationLock } from './PaymentRecalculationLockContext'

export interface Props {
  amountTendered: number | string
  hasValidTenderedAmount: boolean
  filteredPaymentTypes: ApiRetailerPaymentType[]
  systemPaymentTypes: {
    roundingPaymentType?: ApiRetailerPaymentType
    currencyAdjustmentPaymentType?: ApiRetailerPaymentType
    storeCreditPaymentType?: ApiRetailerPaymentType
    loyaltyPaymentType?: ApiRetailerPaymentType
    lspayPaymentType?: ApiRetailerPaymentType
  }
  pay: (payment: ApiRetailerPaymentType) => void
}

export const PaymentOptions: FunctionComponent<Props> = ({
  amountTendered,
  hasValidTenderedAmount,
  filteredPaymentTypes,
  systemPaymentTypes,
  pay,
}: Props) => {
  const t = useT()
  const localizedPaymentTypeName = useLocalizedPaymentTypeName()
  const hotkeysService = useHotkeysService()
  const { arePaymentActionsLocked } = usePaymentRecalculationLock()

  const sortedPaymentTypes = useMemo(
    () =>
      [...filteredPaymentTypes].sort((a, b) =>
        localizedPaymentTypeName(a.name).localeCompare(
          localizedPaymentTypeName(b.name)
        )
      ),
    [filteredPaymentTypes, localizedPaymentTypeName]
  )
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const saleFinals = useAppSelector(selectSaleFinals)
  const lspCashDiscountPercentage = useAppSelector(
    selectLspCashDiscountPercentage
  )
  const lspCashDiscountBlockedSale = useLspCashDiscountBlockedSale()
  const formatCurrencyValue = useFormatCurrencyValue()
  const { checkIfPaymentDisabled } = usePaymentDisabilityCheck(
    amountTendered,
    hasValidTenderedAmount
  )
  const isLSPayAvailable = !!systemPaymentTypes.lspayPaymentType

  // When an LSPay cash discount is applied to the sale, decorate the Cash
  // button label with the discounted amount the customer will actually pay
  // (e.g. "Cash ($96.00)").
  const hasLspCashDiscount = lspCashDiscountPercentage > 0
  const isLspCashDiscountingBlocked = isLspCashDiscountBlocked(
    String(amountTendered),
    lspCashDiscountPercentage,
    lspCashDiscountBlockedSale
  )

  const getPaymentLabel = useCallback(
    (paymentType: ApiRetailerPaymentType) => {
      const name = localizedPaymentTypeName(paymentType.name)
      if (
        !hasLspCashDiscount ||
        isLspCashDiscountingBlocked ||
        paymentType.type_id !== PAYMENT_TYPES.CASH ||
        vn(outstandingBalance).lte(0)
      ) {
        return name
      }
      // The Cash button previews the LSPay-discounted cash price.
      const discountedAmount = applyLspCashDiscountToOutstanding(
        outstandingBalance,
        saleFinals.exclusivePrice,
        lspCashDiscountPercentage
      ).toFixed(2)
      return `${name} (${formatCurrencyValue(discountedAmount)})`
    },
    [
      formatCurrencyValue,
      hasLspCashDiscount,
      isLspCashDiscountingBlocked,
      localizedPaymentTypeName,
      lspCashDiscountPercentage,
      outstandingBalance,
      saleFinals.exclusivePrice,
    ]
  )

  const getHotKey = useCallback(
    (index: number): number => {
      // We start from 1 (not 0).
      if (isLSPayAvailable) {
        // LSPay will be number one if present.
        return index + 2
      }

      return index + 1
    },
    [isLSPayAvailable]
  )

  useEffect(() => {
    const cleanUpFunctions: Array<() => void> = []

    sortedPaymentTypes.forEach((paymentType, index) => {
      const hotKey = getHotKey(index)
      const config = {
        summary: t('Select payment option {hotkey}', {
          _context:
            'Hot key summary for the payment option buttons on the payment screen',
          hotkey: hotKey,
        }),
        combo: `alt+${hotKey}`,
        count: true,
        action: () => {
          if (!arePaymentActionsLocked) {
            pay(paymentType)
          }
        },
      }
      hotkeysService.add(config)
      cleanUpFunctions.push(() => hotkeysService.remove(config.combo))
    })

    return () => {
      cleanUpFunctions.forEach(func => func())
    }
  }, [
    t,
    pay,
    getHotKey,
    hotkeysService,
    sortedPaymentTypes,
    arePaymentActionsLocked,
  ])

  return (
    <div
      id="payment-options-group"
      className="payment-type-button-group vd-btn-group vd-mt4"
    >
      {sortedPaymentTypes.map((paymentType, index) => {
        return (
          <button
            data-hotkey-tip={getHotKey(index)}
            key={paymentType.id}
            data-id={paymentType.type_id}
            className={classNames('vd-btn vd-btn--jumbo btn--payment-type', {
              'vd-btn--no': outstandingBalance < 0,
              'vd-btn--supplementary':
                outstandingBalance >= 0 && systemPaymentTypes.lspayPaymentType,
              'vd-btn--go':
                outstandingBalance >= 0 && !systemPaymentTypes.lspayPaymentType,
            })}
            disabled={
              arePaymentActionsLocked || checkIfPaymentDisabled(paymentType)
            }
            onClick={() => pay(paymentType)}
          >
            {getPaymentLabel(paymentType)}
          </button>
        )
      })}
    </div>
  )
}
